import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import { styled, useTheme, alpha } from '@mui/material/styles';
import type {
  EmblaCarouselType,
  EmblaEventType,
  EmblaOptionsType,
} from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import { useNavigation } from './base-carousel-navigation';
import { usePagination } from './base-carousel-pagination';
import { baseCarouselStyle } from './base-style';
import { AnalyticsItemPositionProvider } from '@/context/analytics-item/provider';
import { useScreenSizes } from '@/hooks/use-screen-sizes';
interface CarouselProps extends EmblaOptionsType {
  className?: string;
  fullWidth?: boolean;
  navigation?: boolean;
  onChange?: (carousel: EmblaCarouselType, eventName: EmblaEventType) => void;
  pagination?: boolean;
  slidesComponents: ReactNode[];
}

interface NavigationButtonProps extends Omit<IconButtonProps, 'color'> {
  active?: boolean;
}

export const NavigationButton: React.FC<NavigationButtonProps> = ({
  active = false,
  ...rest
}) => (
  <IconButton color={active ? 'primary' : 'default'} size="small" {...rest}>
    <FiberManualRecordIcon fontSize="inherit" />
  </IconButton>
);

const StyledNavigationButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(`${theme.palette.neutral[500]}`, 0.3),
  '&:hover': {
    backgroundColor: alpha(`${theme.palette.neutral[500]}`, 0.4),
  },
}));

type NavBtnProps = Omit<IconButtonProps, 'color'> & { disabled?: boolean };

const cx = (base: string, disabled?: boolean) =>
  `${base}${disabled ? ' embla__navigation-button--disabled' : ''}`;

const PrevButton: React.FC<NavBtnProps> = ({ disabled, ...rest }) => (
  <div
    className={cx(
      'embla__navigation-button embla__navigation-prev-button',
      disabled
    )}
  >
    <StyledNavigationButton disabled={disabled} {...rest}>
      <ChevronLeft />
    </StyledNavigationButton>
  </div>
);

const NextButton: React.FC<NavBtnProps> = ({ disabled, ...rest }) => (
  <div
    className={cx(
      'embla__navigation-button embla__navigation-next-button',
      disabled
    )}
  >
    <StyledNavigationButton disabled={disabled} {...rest}>
      <ChevronRight />
    </StyledNavigationButton>
  </div>
);

const Embla = styled('div')(({ theme }) => ({
  ...baseCarouselStyle(theme),
}));

export const Carousel = ({
  className,
  fullWidth = true,
  loop = true,
  navigation = true,
  onChange,
  pagination = false,
  slidesComponents,
  ...otherProps
}: CarouselProps) => {
  const theme = useTheme();
  const { isSmallScreen } = useScreenSizes();

  const baseEmblaOptions = {
    align: 'start',
    dragFree: true,
    loop,
    slidesToScroll: 1,
  };

  if (!fullWidth) {
    Object.assign(baseEmblaOptions, {
      breakpoints: {
        [`(min-width:${theme.breakpoints.values.md}px)`]: { slidesToScroll: 2 },
        [`(min-width:${theme.breakpoints.values.lg}px)`]: { slidesToScroll: 2 },
      },
    });
  }

  const plugins = useMemo(() => {
    try {
      return [WheelGesturesPlugin()];
    } catch {
      return [];
    }
  }, []);

  const [viewportRef, emblaApi] = useEmblaCarousel(
    {
      ...baseEmblaOptions,
      ...otherProps,
    } as EmblaOptionsType,
    plugins
  );

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    usePagination(emblaApi);

  const {
    prevButtonDisabled,
    nextButtonDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = useNavigation(emblaApi);

  useEffect(() => {
    if (!emblaApi) return;
    const root = emblaApi.rootNode();
    const markTouched = () => root.classList.add('is-touched');

    emblaApi.on('select', markTouched);

    if (onChange) {
      emblaApi.on('settle', onChange);
    }

    return () => {
      emblaApi.off('select', markTouched);
      if (onChange) {
        emblaApi.off('settle', onChange);
      }
    };
  }, [emblaApi, onChange]);

  return (
    <Embla className={className} sx={{ overflow: { md: 'hidden' } }}>
      <div className="embla__viewport" ref={viewportRef}>
        <div className="embla__container">
          {slidesComponents.map((slide, i) => (
            <div className="embla__slide" key={i}>
              <AnalyticsItemPositionProvider position={i + 1}>
                {slide}
              </AnalyticsItemPositionProvider>
            </div>
          ))}
        </div>

        {navigation && !isSmallScreen && (
          <>
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevButtonDisabled}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextButtonDisabled}
            />
          </>
        )}
      </div>

      {pagination && (
        <div className="embla__pagination">
          {scrollSnaps.map((_, i) => (
            <NavigationButton
              key={i}
              active={i === selectedIndex}
              onClick={() => onDotButtonClick(i)}
              className="embla__dot"
            />
          ))}
        </div>
      )}
    </Embla>
  );
};
